// WatchlistTab — Multi-watchlist with live prices, BUY/SELL (Angel One Light Theme)
import React from 'react';
import { useTrading } from '../../../context/TradingContext';
import { useTradingWatchlist } from '../../../hooks/useWatchlist';
import PnLBadge from '../shared/PnLBadge';
import { FaPlus, FaTimes } from 'react-icons/fa';

export default function WatchlistTab() {
  const { setOrderModal, setSearchOpen, connectionStatus } = useTrading();
  const { watchlists, activeIndex, items, switchWatchlist, removeFromWatchlist } = useTradingWatchlist();

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-sm text-gray-500">Connect to Angel One to manage watchlists</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Watchlist Tabs */}
      <div className="flex items-center gap-2">
        {watchlists.map((wl, i) => (
          <button
            key={i}
            onClick={() => switchWatchlist(i)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
              activeIndex === i
                ? 'bg-blue-50 text-blue-600 border border-blue-200'
                : 'text-gray-500 hover:bg-gray-50 border border-transparent'
            }`}
          >
            {wl.name}
            <span className="ml-1.5 text-gray-400">({wl.items.length})</span>
          </button>
        ))}
        <button
          onClick={() => setSearchOpen(true)}
          className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-blue-600 hover:bg-blue-50 border border-dashed border-blue-200 flex items-center gap-1"
        >
          <FaPlus className="text-[8px]" /> Add
        </button>
      </div>

      {/* Watchlist Items */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">
          <div className="col-span-3">Symbol</div>
          <div className="col-span-2 text-right">LTP</div>
          <div className="col-span-2 text-right">Change</div>
          <div className="col-span-1 text-right">%</div>
          <div className="col-span-2 text-right">Volume</div>
          <div className="col-span-2 text-center">Actions</div>
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-xs text-gray-400">No instruments in this watchlist</p>
            <button
              onClick={() => setSearchOpen(true)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
            >
              Search and add instruments
            </button>
          </div>
        ) : (
          items.map((item, i) => {
            const change = item.ltp && item.close ? item.ltp - item.close : 0;
            const changePct = item.close ? (change / item.close) * 100 : 0;
            const isUp = change >= 0;

            return (
              <div
                key={`${item.exchange}_${item.token}`}
                className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors group items-center"
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className="col-span-3">
                  <p className="text-xs font-semibold text-gray-800">{item.symbol}</p>
                  <p className="text-[10px] text-gray-400">{item.exchange}</p>
                </div>
                <div className="col-span-2 text-right">
                  <p className="text-xs font-mono font-semibold text-gray-900">
                    {item.ltp ? `₹${item.ltp.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}
                  </p>
                </div>
                <div className="col-span-2 text-right">
                  <PnLBadge value={change} size="sm" />
                </div>
                <div className="col-span-1 text-right">
                  <span className={`text-[10px] font-mono ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                    {changePct.toFixed(2)}%
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-[10px] text-gray-400 font-mono">
                    {item.volume ? item.volume.toLocaleString('en-IN') : '—'}
                  </span>
                </div>
                <div className="col-span-2 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setOrderModal({ instrument: item, side: 'BUY' })}
                    className="px-2 py-1 rounded text-[9px] font-bold bg-green-50 text-green-600 hover:bg-green-100 border border-green-200"
                  >
                    B
                  </button>
                  <button
                    onClick={() => setOrderModal({ instrument: item, side: 'SELL' })}
                    className="px-2 py-1 rounded text-[9px] font-bold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                  >
                    S
                  </button>
                  <button
                    onClick={() => removeFromWatchlist(item.token, item.exchange)}
                    className="px-1.5 py-1 rounded text-[9px] text-gray-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
